import dayjs from 'dayjs';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { bankruptTypesOptions } from 'constants/options';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { BorrowersTestIds } from '../../../../../../Borrowers.testIds';
import { defaultRadioInputProps, getDefaultCheckboxesGroupProps } from '../Declarations.constants';
import { sectionStyles, sectionTitleStyles } from '../Declarations.styles';

import {
  BEFORE_FILING_DATE_MESSAGE,
  CREDIT_EVENT_DATES_GROUP_TITLE,
  CREDIT_EVENT_DATES_OVERRIDDEN_KEY,
  CREDIT_EVENT_DATES_OVERRIDDEN_LABEL,
  CreditEventDateKey,
  FUTURE_DATE_MESSAGE,
} from './Finances.constants';

export const getConfigFields = ({ entityId }) => {
  const modelPath = `[${entityId}].declarations.finances.`;

  return {
    guarantorOnAnyDebtOutsideTheLoan: {
      fieldId: 'guarantorOnAnyDebtOutsideTheLoan',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}guarantorOnAnyDebtOutsideTheLoan`,
      inputProps: {
        label:
          'Are you a co-signer or guarantor on any debt or loan that is not disclosed on this application?',
        dataTestId: BorrowersTestIds.FINANCES_FORM_GUARANTORONANYDEBTOUTSIDETHELOAN_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    outstandingJudgements: {
      fieldId: 'outstandingJudgements',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}outstandingJudgements`,
      inputProps: {
        label: 'Are there any outstanding judgments against you?',
        dataTestId: BorrowersTestIds.FINANCES_FORM_OUTSTANDINGJUDGEMENTS_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    presentlyObligatedOnAnyLoanOrDebt: {
      fieldId: 'presentlyObligatedOnAnyLoanOrDebt',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}presentlyObligatedOnAnyLoanOrDebt`,
      inputProps: {
        label: 'Are you currently delinquent or in default on a Federal debt?',
        dataTestId: BorrowersTestIds.FINANCES_FORM_PRESENTLYOBLIGATEDONANYLOANORDEBT_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    partyToLawSuite: {
      fieldId: 'partyToLawSuite',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}partyToLawSuite`,
      inputProps: {
        label:
          'Are you a party to a lawsuit in which you potentially have any personal financial liability?',
        dataTestId: BorrowersTestIds.FINANCES_FORM_PARTYTOLAWSUITE_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    propertyInLieuOfForeclosureInPastSevenYears: {
      fieldId: 'propertyInLieuOfForeclosureInPastSevenYears',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}propertyInLieuOfForeclosureInPastSevenYears`,
      inputProps: {
        label:
          'Have you conveyed title to any property in lieu of foreclosure in the past 7 years?',
        dataTestId:
          BorrowersTestIds.FINANCES_FORM_PROPERTYINLIEUOFFORECLOSUREINPASTSEVENYEARS_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    foreclosureSoldLessOutstandingBalanceWithinPastSevenYears: {
      fieldId: 'foreclosureSoldLessOutstandingBalanceWithinPastSevenYears',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}foreclosureSoldLessOutstandingBalanceWithinPastSevenYears`,
      inputProps: {
        label:
          'Within the past 7 years, have you completed a pre-foreclosure sale or short sale, whereby the property was sold to a\n' +
          'third party and the Lender agreed to accept less than the outstanding mortgage balance due?',
        dataTestId:
          BorrowersTestIds.FINANCES_FORM_FORECLOSURESOLDLESSOUTSTANDINGBALANCEWITHINPASTSEVENYEARS_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    isLastMonthsForeclosure: {
      fieldId: 'isLastMonthsForeclosure',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}isLastMonthsForeclosure`,
      inputProps: {
        label: 'Have you had property foreclosed upon in the last 7 years?',
        dataTestId: BorrowersTestIds.FINANCES_FORM_ISLASTMONTHSFORECLOSURE_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    isLastMonthsBankruptcy: {
      fieldId: 'isLastMonthsBankruptcy',
      type: FieldType.RADIO,
      nameInModel: `${modelPath}isLastMonthsBankruptcy`,
      inputProps: {
        label: 'Have you declared bankruptcy within the past 7 years?',
        dataTestId: BorrowersTestIds.FINANCES_FORM_ISLASTMONTHSBANKRUPTCY_SELECT,
        ...defaultRadioInputProps,
        required: true,
      },
    },
    bankruptTypes: {
      fieldId: 'bankruptTypes',
      type: FieldType.CHECKBOX_GROUP,
      entityName: 'bankruptcy type',
      nameInModel: `${modelPath}bankruptTypes`,
      inputProps: {
        ...getDefaultCheckboxesGroupProps({
          padding: '8px 0 0 16px',
          checkboxesPadding: '0 0 0 16px',
          withBottomBorder: true,
        }),
        label: 'Identify the type(s) of bankruptcy',
        isMulti: true,
        options: bankruptTypesOptions,
        dataTestId: BorrowersTestIds.FINANCES_FORM_BANKRUPTTYPE_SELECT,
        required: true,
        isRestyled: true,
      },
    },
  };
};

export const getConfig = compose(
  fillConfigFieldsWithKeys,
  ({ entityId, isLastMonthsBankruptcy }) => {
    const financesConfigFields = getConfigFields({
      entityId,
    });

    return {
      groups: [
        {
          title: { label: 'Finances', titleStyles: sectionTitleStyles },
          key: `${entityId}-declarations-finances`,
          type: FieldType.GROUP,
          customStyles: sectionStyles,
          fields: [
            financesConfigFields.guarantorOnAnyDebtOutsideTheLoan,
            financesConfigFields.outstandingJudgements,
            financesConfigFields.presentlyObligatedOnAnyLoanOrDebt,
            financesConfigFields.partyToLawSuite,
            financesConfigFields.propertyInLieuOfForeclosureInPastSevenYears,
            financesConfigFields.foreclosureSoldLessOutstandingBalanceWithinPastSevenYears,
            financesConfigFields.isLastMonthsForeclosure,
            financesConfigFields.isLastMonthsBankruptcy,

            ...getConditionalArray(isLastMonthsBankruptcy, [financesConfigFields.bankruptTypes]),
          ],
        },
      ],
    };
  },
);

const getEventDateField = ({ modelPath, key, label, dataTestId, minDate = null }) => ({
  fieldId: key,
  type: FieldType.DATE,
  nameInModel: `${modelPath}${key}`,
  inputProps: {
    label,
    type: FieldInputType.DATE,
    format: COMMON_DATE_FORMAT,
    variant: 'outlined',
    disabled: false,
    required: false,
    maxDate: dayjs().format(COMMON_DATE_FORMAT),
    maxDateValidationMessage: FUTURE_DATE_MESSAGE,
    ...getConditionalObject(minDate, {
      minDate,
      minDateValidationMessage: BEFORE_FILING_DATE_MESSAGE,
    }),
    dataTestId,
  },
});

const getCreditEventDateFields = ({ entityId, bankruptcyFilingDate }) => {
  const modelPath = `[${entityId}].declarations.finances.`;
  const filingDate =
    bankruptcyFilingDate && dayjs(bankruptcyFilingDate).isValid()
      ? dayjs(bankruptcyFilingDate).format(COMMON_DATE_FORMAT)
      : null;

  return {
    [CreditEventDateKey.BANKRUPTCY_FILING_DATE]: getEventDateField({
      modelPath,
      key: CreditEventDateKey.BANKRUPTCY_FILING_DATE,
      label: 'Bankruptcy filing date',
      dataTestId: BorrowersTestIds.FINANCES_FORM_BANKRUPTCY_FILING_DATE_INPUT,
    }),
    [CreditEventDateKey.BANKRUPTCY_DISCHARGE_DATE]: getEventDateField({
      modelPath,
      key: CreditEventDateKey.BANKRUPTCY_DISCHARGE_DATE,
      label: 'Bankruptcy discharge date',
      dataTestId: BorrowersTestIds.FINANCES_FORM_BANKRUPTCY_DISCHARGE_DATE_INPUT,
      minDate: filingDate,
    }),
    [CreditEventDateKey.BANKRUPTCY_DISMISSAL_DATE]: getEventDateField({
      modelPath,
      key: CreditEventDateKey.BANKRUPTCY_DISMISSAL_DATE,
      label: 'Bankruptcy dismissal date',
      dataTestId: BorrowersTestIds.FINANCES_FORM_BANKRUPTCY_DISMISSAL_DATE_INPUT,
      minDate: filingDate,
    }),
    [CreditEventDateKey.FORECLOSURE_COMPLETION_DATE]: getEventDateField({
      modelPath,
      key: CreditEventDateKey.FORECLOSURE_COMPLETION_DATE,
      label: 'Foreclosure completion date',
      dataTestId: BorrowersTestIds.FINANCES_FORM_FORECLOSURE_COMPLETION_DATE_INPUT,
    }),
    [CreditEventDateKey.SHORT_SALE_DATE]: getEventDateField({
      modelPath,
      key: CreditEventDateKey.SHORT_SALE_DATE,
      label: 'Short sale date',
      dataTestId: BorrowersTestIds.FINANCES_FORM_SHORT_SALE_DATE_INPUT,
    }),
    [CreditEventDateKey.DEED_IN_LIEU_DATE]: getEventDateField({
      modelPath,
      key: CreditEventDateKey.DEED_IN_LIEU_DATE,
      label: 'Deed-in-lieu date',
      dataTestId: BorrowersTestIds.FINANCES_FORM_DEED_IN_LIEU_DATE_INPUT,
    }),
    [CREDIT_EVENT_DATES_OVERRIDDEN_KEY]: {
      fieldId: CREDIT_EVENT_DATES_OVERRIDDEN_KEY,
      type: FieldType.SWITCH,
      nameInModel: `${modelPath}${CREDIT_EVENT_DATES_OVERRIDDEN_KEY}`,
      inputProps: {
        label: CREDIT_EVENT_DATES_OVERRIDDEN_LABEL,
        disabled: false,
        required: false,
        columns: 3,
        dataTestId: BorrowersTestIds.FINANCES_FORM_CREDIT_EVENT_DATES_OVERRIDDEN_SWITCH,
      },
    },
  };
};

export const getCreditEventDatesConfig = compose(
  fillConfigFieldsWithKeys,
  ({ entityId, visibleDateKeys, bankruptcyFilingDate }) => {
    const fields = getCreditEventDateFields({ entityId, bankruptcyFilingDate });

    // The group keeps its key while empty so the modal's merged config drops its old fields.
    return {
      groups: [
        {
          title: { label: CREDIT_EVENT_DATES_GROUP_TITLE, titleStyles: sectionTitleStyles },
          key: `${entityId}-declarations-finances-credit-event-dates`,
          type: FieldType.GROUP,
          fields: getConditionalArray(visibleDateKeys.length > 0, () => [
            ...visibleDateKeys.map(key => fields[key]),
            fields[CREDIT_EVENT_DATES_OVERRIDDEN_KEY],
          ]),
        },
      ],
    };
  },
);
